import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { get, post } from '../lib/api';
import { addPlatformFeatures } from '../lib/platformMapLayers';

const latLng = point => point?.type === 'Point' && point.coordinates?.length === 2
  ? [point.coordinates[1], point.coordinates[0]] : null;
const point = position => ({ type: 'Point', coordinates: [position.lng, position.lat] });

export default function LocationPicker({ value, onChange, label = 'Delivery location', pointerLabel = 'Deliver here', disabled = false, resolveAddress = false, viewOnly = false, mapHeight = 280, savedAddressControls = false, actionsContainer = null, labelAction = null, hideGuidance = false, addressLookupEndpoint = '/customers/reverse-geocode' }) {
  const container = useRef(null), map = useRef(null), marker = useRef(null);
  const change = useRef(onChange), disabledRef = useRef(disabled);
  const currentDraft = useRef(value);
  change.current = onChange;
  disabledRef.current = disabled || viewOnly;
  const [draft, setDraft] = useState(value);
  currentDraft.current = draft;
  const [error, setError] = useState('');
  const [locating, setLocating] = useState(false);
  const [resolved, setResolved] = useState(null);
  const [lookupRevision, setLookupRevision] = useState(0);
  const draftKey = JSON.stringify(draft);
  useEffect(() => {
    if (!resolveAddress || !draftKey) return undefined;
    let active = true;
    setError('');
    const timer = setTimeout(() => {
      post(addressLookupEndpoint, { location: JSON.parse(draftKey) }).then(result => {
        if (active) setResolved({ key: draftKey, completeAddress: result.completeAddress, nearestPoint: result.nearestPoint });
      }).catch(error => {
        if (active) { setResolved({ key: draftKey, error: true }); setError(error.response?.data?.message || 'Unable to generate the address. Move the pin or try again.'); }
      });
    }, 600);
    return () => { active = false; clearTimeout(timer); };
  }, [draftKey, resolveAddress, lookupRevision, addressLookupEndpoint]);
  const live = useRef(false);
  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => {
    live.current = true;
    const instance = L.map(container.current).setView([8.51433, 123.09492], 16);
    map.current = instance;
    let active = true;
    get('/platform-map/settings').then(({ settings }) => {
      if (active && !currentDraft.current) instance.setView([settings.latitude, settings.longitude], settings.zoom);
    }).catch(() => { if (active) setError('Unable to load the configured map view. Reload to try again.'); });
    get('/platform-map').then(result => { if (active) addPlatformFeatures(instance, result.features); })
      .catch(() => { if (active) setError('Platform roads and points could not load. Sign in or reload to try again.'); });
    const tiles = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors', maxZoom: 19,
    }).addTo(instance);
    tiles.on('tileerror', () => setError('Map tiles could not load. Check your connection and try again.'));
    instance.on('click', event => { if (!disabledRef.current) setDraft(point(event.latlng)); });
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => instance.invalidateSize());
    resize?.observe(container.current);
    return () => { active = false; live.current = false; resize?.disconnect(); instance.remove(); map.current = null; marker.current = null; };
  }, []);
  useEffect(() => {
    if (!map.current) return;
    const position = latLng(draft);
    if (!position) { marker.current?.remove(); marker.current = null; return; }
    if (!marker.current) {
      marker.current = L.marker(position, {
        draggable: !disabled && !viewOnly,
        icon: L.divIcon({ className: '', html: '<span style="display:block;width:24px;height:24px;background:#dc2626;border:3px solid white;border-radius:50%;box-shadow:0 1px 5px #333"></span>', iconSize: [24, 24], iconAnchor: [12, 12] }),
      }).addTo(map.current);
      if (resolveAddress) marker.current.bindTooltip(pointerLabel, { permanent: true, direction: 'top', offset: [0, -12] });
      marker.current.on('dragend', () => setDraft(point(marker.current.getLatLng())));
    } else marker.current.setLatLng(position);
    if (disabled || viewOnly) marker.current.dragging.disable(); else marker.current.dragging.enable();
    map.current.panTo(position);
  }, [draft, disabled, viewOnly, resolveAddress, pointerLabel]);
  const gps = () => {
    setError('');
    if (!navigator.geolocation) { setError('GPS is unavailable in this browser. Tap the map to select a pin.'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(position => {
      if (!live.current) return;
      setLocating(false);
      setDraft({ type: 'Point', coordinates: [position.coords.longitude, position.coords.latitude] });
      map.current?.setZoom(17);
    }, () => {
      if (!live.current) return;
      setLocating(false);
      setError('Unable to access GPS. Allow location access over HTTPS, or tap the map.');
    }, { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
  };
  const addressReady = !resolveAddress || Boolean(resolved?.key === draftKey && resolved?.completeAddress);
  const confirmed = Boolean(draft && draftKey === JSON.stringify(value) && addressReady);
  const lookupFailed = Boolean(resolveAddress && resolved?.key === draftKey && resolved?.error);
  const retryLookup = () => { setResolved(null); setLookupRevision(revision => revision + 1); };
  const confirmLocation = () => resolveAddress ? change.current(draft, { completeAddress: resolved.completeAddress }) : change.current(draft);
  const savedAddressAction = <button type="button" disabled={disabled || !draft || confirmed || (!lookupFailed && !addressReady)} onClick={lookupFailed ? retryLookup : confirmLocation} className={`rounded border px-3 py-2 text-sm ${addressReady ? 'border-emerald-300 bg-emerald-100 text-emerald-900' : 'border-slate-200 bg-slate-50 text-slate-500'}`}>{lookupFailed ? 'Retry address lookup' : confirmed ? 'Location confirmed' : 'Confirm selected location'}</button>;
  if (viewOnly) return <div className="space-y-2"><p className="text-sm font-medium">{label}</p><div ref={container} aria-label={`${label} map`} style={{ height: mapHeight, position: 'relative', zIndex: 0 }} />{error && <p role="alert" className="text-sm text-red-600">{error}</p>}</div>;
  return <div className="space-y-2">
    <div className="flex flex-wrap items-center justify-between gap-3"><p className="text-sm font-medium">{label}</p>{labelAction}</div>
    <p className="text-xs text-gray-600">Tap the map or drag the red pin, then confirm the location.</p>
    <div ref={container} aria-label={`${label} map`} className="w-full overflow-hidden rounded-xl border border-slate-200" style={{ height: mapHeight, position: 'relative', zIndex: 0 }} />
    {savedAddressControls ? actionsContainer && createPortal(savedAddressAction, actionsContainer) : <div className="flex flex-wrap items-center gap-2">
      <button type="button" disabled={disabled || locating} onClick={gps} className="rounded border px-3 py-2 text-sm">{locating ? 'Locating...' : 'Use my current GPS location'}</button>
      {draft && <button type="button" disabled={disabled || confirmed || !addressReady} onClick={confirmLocation} className={`rounded border px-3 py-2 text-sm ${addressReady ? 'border-emerald-300 bg-emerald-100 text-emerald-900' : 'border-slate-200 bg-slate-50 text-slate-500'}`}>{confirmed ? 'Location confirmed' : 'Confirm selected location'}</button>}
    </div>}
    {draft && <p className="text-sm">Latitude {draft.coordinates[1].toFixed(6)}, longitude {draft.coordinates[0].toFixed(6)}</p>}
    {resolveAddress && draft && resolved?.key !== draftKey && <p role="status">Finding the address...</p>}
    {resolveAddress && resolved?.key === draftKey && resolved?.completeAddress && <p className="text-sm">{resolved.completeAddress}</p>}
    {resolveAddress && resolved?.key === draftKey && resolved?.nearestPoint && <p className="text-xs text-gray-600">Nearest platform point: {resolved.nearestPoint.name} ({resolved.nearestPoint.distanceMeters} m away)</p>}
    {!savedAddressControls && lookupFailed && <button type="button" disabled={disabled} onClick={retryLookup} className="rounded border px-3 py-2 text-sm">Retry address lookup</button>}
    {!error && !hideGuidance && !value && <p className="text-xs text-amber-700">A confirmed pin is required for delivery checkout.</p>}
    {!error && !hideGuidance && draft && !confirmed && <p role="alert" className="text-xs text-amber-700">Confirm this pin before saving. Your saved pin has not changed yet.</p>}
    <input aria-label="Location confirmation" tabIndex={-1} required={Boolean(draft && !confirmed)} value={draft && !confirmed ? '' : 'confirmed'} onChange={() => {}} style={{ opacity: 0, height: 1, width: 1, position: 'absolute' }} />
    {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
  </div>;
}
